/**
 * フォーム統計のチャート描画コンポーネント (recharts ラッパー)。
 * v3.1.4
 *
 * 10種類の chartType に対応:
 *   bar / bar_horizontal / bar_stacked / pie / donut / line / area / radar / scatter / metric
 */
import type {
  FieldAggregation,
  FormAnalyticsChartType,
} from '@goenhub/shared';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

import type { VisualStylePreset } from './visualStyles';

interface ChartRendererProps {
  chartType: FormAnalyticsChartType;
  aggregations: FieldAggregation[];
  style: VisualStylePreset;
  /** 描画高さ (px、metric 以外)。 */
  height?: number;
}

const DEFAULT_HEIGHT = 280;

/**
 * 集計済み optionCounts、または textValues / dateValues / numericValues からの
 * 頻度集計を { name, value }[] に正規化する。
 *
 * 優先順:
 *   1. optionCounts (select/radio/checkbox/consent でサーバ側が事前集計済み)
 *   2. textValues / dateValues / numericValues の出現頻度
 */
function categoricalCounts(agg: FieldAggregation): Record<string, number> {
  if (agg.optionCounts && Object.keys(agg.optionCounts).length > 0) {
    return agg.optionCounts;
  }
  const counts: Record<string, number> = {};
  const pushAll = (arr: Array<string | number>) => {
    for (const raw of arr) {
      const key = String(raw).trim();
      if (!key) continue;
      counts[key] = (counts[key] ?? 0) + 1;
    }
  };
  if (agg.textValues && agg.textValues.length > 0) pushAll(agg.textValues);
  else if (agg.dateValues && agg.dateValues.length > 0) pushAll(agg.dateValues);
  else if (agg.numericValues && agg.numericValues.length > 0)
    pushAll(agg.numericValues);
  return counts;
}

/**
 * 1問を { name, value } 形式に変換。
 */
function optionToData(agg: FieldAggregation): Array<{ name: string; value: number }> {
  const counts = categoricalCounts(agg);
  return Object.entries(counts).map(([name, value]) => ({ name, value }));
}

/**
 * 複数問を { option, [fieldLabel1]: count, [fieldLabel2]: count } 形式に変換。
 * 全選択肢の和集合をキーとする。
 */
function multiOptionToData(aggs: FieldAggregation[]): {
  data: Array<Record<string, string | number>>;
  fieldKeys: string[];
} {
  const perField = aggs.map((a) => categoricalCounts(a));
  const allOptions = new Set<string>();
  for (const counts of perField) {
    for (const k of Object.keys(counts)) allOptions.add(k);
  }
  const fieldKeys = aggs.map((a) => a.label);
  const data = Array.from(allOptions).map((opt) => {
    const row: Record<string, string | number> = { name: opt };
    perField.forEach((counts, i) => {
      row[fieldKeys[i]] = counts[opt] ?? 0;
    });
    return row;
  });
  return { data, fieldKeys };
}

/**
 * 空データのフォールバックメッセージ。
 */
function NoDataMessage({
  height,
  message,
  textColor,
}: {
  height: number;
  message: string;
  textColor: string;
}) {
  return (
    <div
      className="flex items-center justify-center text-xs text-center px-4"
      style={{ minHeight: height, color: textColor, opacity: 0.7 }}
    >
      {message}
    </div>
  );
}

/**
 * 数値系を簡易ヒストグラム ({ bin, count }[]) に変換。
 */
function numericToHistogram(values: number[], binCount = 8) {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return [{ name: String(min), value: values.length }];
  const span = max - min;
  const binSize = span / binCount;
  const bins = new Array(binCount).fill(0);
  for (const v of values) {
    let idx = Math.floor((v - min) / binSize);
    if (idx >= binCount) idx = binCount - 1;
    bins[idx]++;
  }
  return bins.map((count, i) => ({
    name: `${(min + i * binSize).toFixed(0)}–${(min + (i + 1) * binSize).toFixed(0)}`,
    value: count,
  }));
}

/**
 * 日付系を月別件数に変換。
 */
function dateToTimeSeries(values: string[]) {
  const counts = new Map<string, number>();
  for (const v of values) {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, value]) => ({ name, value }));
}

export function ChartRenderer({
  chartType,
  aggregations,
  style,
  height = DEFAULT_HEIGHT,
}: ChartRendererProps) {
  // 集計対象が空ならフォールバック
  if (!aggregations || aggregations.length === 0) {
    return (
      <div className="text-xs text-center py-12" style={{ color: style.textColor }}>
        集計対象の設問が見つかりません。
      </div>
    );
  }

  // 1問のみのチャートで使うデータ
  const single = aggregations[0];

  // ─────────────────────────────────────────────
  // METRIC (数値カード)
  // ─────────────────────────────────────────────
  if (chartType === 'metric') {
    const isNumeric = single.numericValues && single.numericValues.length > 0;
    const value = isNumeric
      ? (
          single.numericValues!.reduce((a, b) => a + b, 0) /
          single.numericValues!.length
        ).toFixed(1)
      : single.answeredCount;
    const label = isNumeric ? '平均値' : '回答数';
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: height, color: style.titleColor }}
      >
        <div
          className="text-5xl font-bold tabular-nums"
          style={{ color: style.palette[0] }}
        >
          {value}
        </div>
        <div
          className="text-xs mt-2 uppercase tracking-wider"
          style={{ color: style.textColor }}
        >
          {label} ({single.label})
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // PIE / DONUT (1問の選択肢分布)
  // ─────────────────────────────────────────────
  if (chartType === 'pie' || chartType === 'donut') {
    const data = optionToData(single);
    if (data.length === 0) {
      return (
        <NoDataMessage
          height={height}
          message="集計対象の回答がありません。別のチャート種類をお試しください。"
          textColor={style.textColor}
        />
      );
    }
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={Math.min(height / 2 - 30, 110)}
            innerRadius={chartType === 'donut' ? 50 : 0}
            label={(entry) => `${entry.name}: ${entry.value}`}
            labelLine={false}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={style.palette[i % style.palette.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: style.cardBackground,
              border: `1px solid ${style.cardBorder}`,
              color: style.textColor,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // ─────────────────────────────────────────────
  // SCATTER (数値系のみ、index vs value)
  // ─────────────────────────────────────────────
  if (chartType === 'scatter') {
    const xValues = single.numericValues ?? [];
    const data = xValues.map((v, i) => ({ x: i + 1, y: v }));
    if (data.length === 0) {
      return (
        <NoDataMessage
          height={height}
          message="散布図は数値型 (number) の設問でのみ表示できます。"
          textColor={style.textColor}
        />
      );
    }
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart>
          <CartesianGrid stroke={style.gridColor} />
          <XAxis
            dataKey="x"
            type="number"
            stroke={style.textColor}
            tick={{ fontSize: 11 }}
            name="No"
          />
          <YAxis
            dataKey="y"
            type="number"
            stroke={style.textColor}
            tick={{ fontSize: 11 }}
            name={single.label}
          />
          <ZAxis range={[60, 60]} />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            contentStyle={{
              backgroundColor: style.cardBackground,
              border: `1px solid ${style.cardBorder}`,
              color: style.textColor,
            }}
          />
          <Scatter data={data} fill={style.palette[0]} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  // ─────────────────────────────────────────────
  // RADAR (1問または複数問の選択肢)
  // ─────────────────────────────────────────────
  if (chartType === 'radar') {
    const data =
      aggregations.length === 1
        ? optionToData(single).map(({ name, value }) => ({ name, value }))
        : multiOptionToData(aggregations).data;
    const seriesKeys =
      aggregations.length === 1
        ? ['value']
        : multiOptionToData(aggregations).fieldKeys;
    if (data.length === 0) {
      return (
        <NoDataMessage
          height={height}
          message="集計対象の回答がありません。"
          textColor={style.textColor}
        />
      );
    }
    return (
      <ResponsiveContainer width="100%" height={height}>
        <RadarChart data={data}>
          <PolarGrid stroke={style.gridColor} />
          <PolarAngleAxis
            dataKey="name"
            tick={{ fill: style.textColor, fontSize: 11 }}
          />
          <PolarRadiusAxis tick={{ fill: style.textColor, fontSize: 10 }} />
          {seriesKeys.map((k, i) => (
            <Radar
              key={k}
              name={aggregations.length === 1 ? single.label : k}
              dataKey={k}
              stroke={style.palette[i % style.palette.length]}
              fill={style.palette[i % style.palette.length]}
              fillOpacity={0.4}
            />
          ))}
          <Legend wrapperStyle={{ color: style.textColor, fontSize: 11 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: style.cardBackground,
              border: `1px solid ${style.cardBorder}`,
              color: style.textColor,
            }}
          />
        </RadarChart>
      </ResponsiveContainer>
    );
  }

  // ─────────────────────────────────────────────
  // LINE / AREA (時系列または数値ヒストグラム)
  // ─────────────────────────────────────────────
  if (chartType === 'line' || chartType === 'area') {
    let data: Array<{ name: string; value: number }>;
    if (single.dateValues && single.dateValues.length > 0) {
      data = dateToTimeSeries(single.dateValues);
    } else if (single.numericValues && single.numericValues.length > 0) {
      data = numericToHistogram(single.numericValues);
    } else {
      data = optionToData(single);
    }
    if (data.length === 0) {
      return (
        <NoDataMessage
          height={height}
          message="集計対象の回答がありません。"
          textColor={style.textColor}
        />
      );
    }
    const ChartCmp = chartType === 'line' ? LineChart : AreaChart;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ChartCmp data={data}>
          <CartesianGrid stroke={style.gridColor} strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            stroke={style.textColor}
            tick={{ fontSize: 11 }}
          />
          <YAxis stroke={style.textColor} tick={{ fontSize: 11 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: style.cardBackground,
              border: `1px solid ${style.cardBorder}`,
              color: style.textColor,
            }}
          />
          {chartType === 'line' ? (
            <Line
              type="monotone"
              dataKey="value"
              stroke={style.palette[0]}
              strokeWidth={2}
              dot={{ fill: style.palette[0], r: 3 }}
            />
          ) : (
            <Area
              type="monotone"
              dataKey="value"
              stroke={style.palette[0]}
              fill={style.palette[0]}
              fillOpacity={0.4}
            />
          )}
        </ChartCmp>
      </ResponsiveContainer>
    );
  }

  // ─────────────────────────────────────────────
  // BAR / BAR_HORIZONTAL / BAR_STACKED
  // ─────────────────────────────────────────────
  // 1問の場合は単一系列、複数問の場合は積み上げ or 並列
  if (aggregations.length === 1) {
    let data: Array<{ name: string; value: number }>;
    if (single.numericValues && single.numericValues.length > 0) {
      data = numericToHistogram(single.numericValues);
    } else if (single.dateValues && single.dateValues.length > 0) {
      data = dateToTimeSeries(single.dateValues);
    } else {
      data = optionToData(single);
    }
    if (data.length === 0) {
      return (
        <NoDataMessage
          height={height}
          message="集計対象の回答がありません。"
          textColor={style.textColor}
        />
      );
    }
    const isHorizontal = chartType === 'bar_horizontal';
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout={isHorizontal ? 'vertical' : 'horizontal'}>
          <CartesianGrid stroke={style.gridColor} strokeDasharray="3 3" />
          {isHorizontal ? (
            <>
              <XAxis type="number" stroke={style.textColor} tick={{ fontSize: 11 }} />
              <YAxis
                type="category"
                dataKey="name"
                stroke={style.textColor}
                tick={{ fontSize: 11 }}
                width={120}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="name"
                stroke={style.textColor}
                tick={{ fontSize: 11 }}
              />
              <YAxis stroke={style.textColor} tick={{ fontSize: 11 }} />
            </>
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: style.cardBackground,
              border: `1px solid ${style.cardBorder}`,
              color: style.textColor,
            }}
            cursor={{ fill: 'transparent' }}
          />
          <Bar dataKey="value" fill={style.palette[0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={style.palette[i % style.palette.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // 複数問: 積み上げ or 横並び
  const { data, fieldKeys } = multiOptionToData(aggregations);
  if (data.length === 0) {
    return (
      <NoDataMessage
        height={height}
        message="集計対象の回答がありません。"
        textColor={style.textColor}
      />
    );
  }
  const isHorizontal = chartType === 'bar_horizontal';
  const isStacked = chartType === 'bar_stacked';
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={isHorizontal ? 'vertical' : 'horizontal'}>
        <CartesianGrid stroke={style.gridColor} strokeDasharray="3 3" />
        {isHorizontal ? (
          <>
            <XAxis type="number" stroke={style.textColor} tick={{ fontSize: 11 }} />
            <YAxis
              type="category"
              dataKey="name"
              stroke={style.textColor}
              tick={{ fontSize: 11 }}
              width={120}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="name"
              stroke={style.textColor}
              tick={{ fontSize: 11 }}
            />
            <YAxis stroke={style.textColor} tick={{ fontSize: 11 }} />
          </>
        )}
        <Tooltip
          contentStyle={{
            backgroundColor: style.cardBackground,
            border: `1px solid ${style.cardBorder}`,
            color: style.textColor,
          }}
          cursor={{ fill: 'transparent' }}
        />
        <Legend wrapperStyle={{ color: style.textColor, fontSize: 11 }} />
        {fieldKeys.map((k, i) => (
          <Bar
            key={k}
            dataKey={k}
            stackId={isStacked ? 'stack' : undefined}
            fill={style.palette[i % style.palette.length]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// 補助: chartType の表示名
export const CHART_TYPE_LABEL: Record<FormAnalyticsChartType, string> = {
  bar: '棒グラフ',
  bar_horizontal: '横棒グラフ',
  bar_stacked: '積み上げ棒グラフ',
  pie: 'パイチャート',
  donut: 'ドーナツチャート',
  line: '折れ線グラフ',
  area: 'エリアチャート',
  radar: 'レーダーチャート',
  scatter: '散布図',
  metric: '数値カード',
};
